import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface Palette {
    quaternary: {
      main: string;
      light: string;
      lighter: string;
      darker: string;
      dark: string;
      blue: string;
      darkBlue: string;
      lightGreen: string;
      darkGreen: string;
      success: string;
      customTurquoise: string;
      dodgerBlue: string;
      bottleGreen: string;
      navyBlue: string;
      lightBlue: string;
      ceruleanBlue: string;
      royalBlue: string;
    };

    tertiary: {
      light: string;
      shadyText: string;
    };
  }
}

// SETUP COLORS

const GREY = {
  50: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};
const PRIMARY = {
  lighter: '#FFD8B2',
  darker: '#993F00',
  contrastText: '#FFFFFF',
  extraLight: '#F06060',
  light: '#F61111',
  dark: '#912020',
  main: '#B82626',
};

const SECONDARY = {
  lighter: '#05A4D4',
  darker: '#00242D',
  contrastText: '#FFFFFF',
  light: '#FBC4C4',
  main: '#912020',
  dark: '#bfebff',
};

const TERTIARY = {
  lighter: '#BDA5CC',
  light: '#8B62A6',
  main: '#591F80',
  dark: '#3E165A',
  darker: '#3E165A',
  contrastText: '#FFFFFF',
  shadyText: '#565656',

};

const INFO = {
  lighter: '#BFEBFA',
  light: '#89DAF5',
  main: '#6FC6CF',
  dark: '#11A3D4',
  darker: '#0D7FA5',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#D8FBDE',
  light: '#86E8AB',
  main: '#008a09',
  dark: '#1B806A',
  darker: '#0A5554',
  contrastText: '#FFFFFF',
};

const WARNING = {
  lighter: '#F3EADD',
  light: '#B1751B',
  main: '#cf9700',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFCEC7',
  light: '#FF9282',
  main: '#E34932',
  dark: '#942B1B',
  darker: '#63160A',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: { black: '#000000', white: '#FFFFFF', grey: '#D7D7D74A' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  tertiary: TERTIARY,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: { paper: '#FFFFFF', default: '#FFFFFF', neutral: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.16),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  } as const;

  return themeMode === 'light' ? light : dark;
}
